// Path: src/pages/student/StudentPortal.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStudentCourses, type PublicCourseSummaryOut } from '../../lib/apis_publish';
import { Play, Layers, Clock } from 'lucide-react';
import LogoutButton from '../../components/common/LogoutButton';
import ExpandableText from '../../components/common/ExpandableText';
import logo from '../../assets/imgs/logo.png';
import { useCoursePermission, VIBE_CODING_COURSE_ID, AI_VEX_COURSE_ID } from '../../hooks/useCoursePermission';

const StudentPortal: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<PublicCourseSummaryOut[]>([]);
  const [activeCourseId, setActiveCourseId] = useState<number | null>(null);
  const [expandedStageIds, setExpandedStageIds] = useState<number[]>([]);

  const vibeCodingPermission = useCoursePermission(VIBE_CODING_COURSE_ID);
  const aiVexPermission = useCoursePermission(AI_VEX_COURSE_ID);

  useEffect(() => {
    // 获取课程数据
    getStudentCourses().then(res => {
      setCourses(res.data);
      if (res.data.length > 0) {
        setActiveCourseId(res.data[0].id);
        // 默认展开第一个阶段
        if (res.data[0].stages.length > 0) {
          const firstStageId = res.data[0].stages[0].id;
          setExpandedStageIds([firstStageId]);
          // 存储到localStorage
          localStorage.setItem('expandedStageIds', JSON.stringify([firstStageId]));
        }
      }
    });
  }, []);

  // 从localStorage加载展开状态
  useEffect(() => {
    const savedExpanded = localStorage.getItem('expandedStageIds');
    if (savedExpanded) {
      try {
        const parsed = JSON.parse(savedExpanded);
        if (Array.isArray(parsed)) {
          setExpandedStageIds(parsed);
        }
      } catch (error) {
        console.error('Failed to parse expandedStageIds:', error);
      }
    }
  }, []);

  // 处理阶段展开/折叠
  const handleStageToggle = (stageId: number) => {
    // 先关闭所有阶段，再展开当前点击的阶段
    setExpandedStageIds([stageId]);
    // 存储到localStorage
    localStorage.setItem('expandedStageIds', JSON.stringify([stageId]));
  };

  const activeCourse = courses.find(c => c.id === activeCourseId);

  return (
    <div className="min-h-screen bg-slate-900 text-white selection:bg-purple-500 selection:text-white font-sans overflow-x-hidden">
      {/* 背景动态光效 */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-purple-900/30 rounded-full blur-[120px]" />
        <div className="absolute bottom-[0%] right-[-10%] w-[40%] h-[60%] bg-blue-900/20 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className="w-full px-6 py-8 flex justify-between items-center backdrop-blur-md sticky top-0 z-20 bg-slate-900/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto w-full flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="h-16 bg-white rounded-lg flex items-center justify-center p-2">
              <img src={logo} alt="Logo" className="h-full object-contain" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">NEXUS <span className="text-blue-400 font-light">Learning</span></h1>
          </div>
          <div className="flex items-center gap-4">
            {/* VibeCoding 跳转按钮 - 仅当有权限时显示 */}
            {vibeCodingPermission.hasAccess && (
              <button
                onClick={() => window.open('/student/vibe-coding', '_blank', 'noopener,noreferrer')}
                className="flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-medium shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300"
                title="进入 VibeCoding 编程学习"
              >
                <span className="text-2xl">🤖</span>
                VibeCoding
              </button>
            )}
            {/* AI VEX 跳转按钮 - 仅当有权限时显示 */}
            {aiVexPermission.hasAccess && (
              <button
                onClick={() => window.open('/student/ai-vex', '_blank', 'noopener,noreferrer')}
                className="flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-all duration-300"
                title="进入 AI VEX"
              >
                <span className="text-2xl">🎮</span>
                AI VEX
              </button>
            )}
            <LogoutButton />
          </div>
        </div>
      </header>

      {/* 主内容区 - 占满整个宽度 */}
      <main className="w-full px-6 py-12">
        {/* 课程切换 Tabs (类似星球切换) */}
        <div className="flex gap-4 overflow-x-auto pb-6 mb-8 scrollbar-hide">
          {courses.map(course => (
            <button
              key={course.id}
              onClick={() => setActiveCourseId(course.id)}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap border ${activeCourseId === course.id
                  ? 'bg-white text-slate-900 border-white shadow-[0_0_20px_rgba(255,255,255,0.3)]'
                  : 'bg-white/5 text-gray-400 border-white/5 hover:bg-white/10 hover:text-white'
                }`}
            >
              {course.title}
            </button>
          ))}
        </div>

        {activeCourse && (
          <div className="space-y-16 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* 课程简介 */}
            <div className="">
              <h2 className="text-5xl font-bold mb-6 leading-tight bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                {activeCourse.title}
              </h2>
              <p className="text-xl text-gray-400 leading-relaxed border-l-4 border-blue-500 pl-6">
                {activeCourse.description}
              </p>
            </div>

            {/* 阶段与课时 (Timeline Layout) */}
            <div className="relative border-l border-white/10 ml-4 md:ml-10 space-y-12 pb-12">
              {activeCourse.stages.map((stage) => (
                <div key={stage.id} className="relative pl-8 md:pl-12">
                  {/* Stage Marker */}
                  <div className="absolute -left-[17px] top-0 w-9 h-9 rounded-full bg-slate-900 border-4 border-blue-600 flex items-center justify-center z-10 cursor-pointer" onClick={() => handleStageToggle(stage.id)}>
                    <span className="text-xs font-bold">{expandedStageIds.includes(stage.id) ? '−' : '+'}</span>
                  </div>

                  <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-3 cursor-pointer" onClick={() => handleStageToggle(stage.id)}>
                    {stage.title}
                    <span className="text-xs bg-blue-500/10 text-blue-400 px-2 py-1 rounded border border-blue-500/20">
                      STAGE {stage.sort_order}
                    </span>
                  </h3>

                  {/* Lessons Grid - 每行显示4节lesson */}
                  {expandedStageIds.includes(stage.id) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    {stage.lessons.map((lesson) => (
                      <div
                        key={lesson.id}
                        onClick={() => navigate(`/student/lesson/${lesson.id}`)}
                        className="group relative bg-white/5 border border-white/5 rounded-2xl overflow-hidden hover:border-blue-500/50 hover:shadow-[0_0_30px_rgba(59,130,246,0.15)] transition-all duration-300 cursor-pointer flex flex-col h-full"
                      >
                        {/* 封面图区域 */}
                        <div className="h-56 bg-slate-800 relative overflow-hidden">
                          {lesson.cover_image_url ? (
                            <img
                              src={lesson.cover_image_url}
                              alt={lesson.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                              // 移除 onError={(e) => e.currentTarget.style.display = 'none'} 
                              // 改为打印错误，方便调试
                              onError={() => console.error("Image Load Failed:", lesson.cover_image_url)}
                            />
                          ) : (
                            /* 没有图片时的默认占位 */
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 group-hover:from-blue-900/40 group-hover:to-purple-900/40 transition-colors">
                              <Layers className="text-white/20 group-hover:text-white/40 transition-colors" size={48} />
                            </div>
                          )}
                          {/* Play Button Overlay */}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/40 backdrop-blur-[2px]">
                            <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center pl-1 shadow-lg transform scale-50 group-hover:scale-100 transition-transform duration-300">
                              <Play className="text-slate-900 fill-current" size={24} />
                            </div>
                          </div>
                        </div>

                        {/* 内容区域 */}
                        <div className="p-5 flex-1 flex flex-col">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs font-mono bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">LESSON {lesson.sort_order}</span>
                          </div>
                          <h4 className="text-lg font-bold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r from-blue-300 to-cyan-300 transition-all duration-300 mb-3 line-clamp-2">
                            {lesson.title}
                          </h4>
                          <div className="text-sm mb-4 flex-1 space-y-2">
                            {lesson.learning_objectives ? (
                              <div className="space-y-2">
                                {(() => {
                                  try {
                                    // 解析JSON格式的学习目标
                                    const objectives = JSON.parse(lesson.learning_objectives);
                                    return (
                                      <>
                                        {/* 核心能力模型 */}
                                        {objectives["核心能力模型"] && (
                                          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2 text-blue-300 text-xs">
                                            <span className="font-bold text-blue-200">核心能力模型:</span> {objectives["核心能力模型"]}
                                          </div>
                                        )}
                                        {/* 知识点拆解 */}
                                        {objectives["知识点拆解"] && (
                                          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-2 text-purple-300 text-xs">
                                            <span className="font-bold text-purple-200">知识点拆解:</span> {objectives["知识点拆解"]}
                                          </div>
                                        )}
                                        {/* AI实战项目 */}
                                        {objectives["AI实战项目"] && (
                                          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2 text-green-300 text-xs">
                                            <ExpandableText
                                              text={objectives["AI实战项目"]}
                                              maxLines={4}
                                              label="AI实战项目: "
                                              labelClassName="font-bold text-green-200"
                                              className="text-green-300"
                                            />
                                          </div>
                                        )}
                                        {/* 解决痛点 */}
                                        {objectives["解决痛点"] && (
                                          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-2 text-orange-300 text-xs">
                                            <span className="font-bold text-orange-200">解决痛点:</span> {objectives["解决痛点"]}
                                          </div>
                                        )}
                                        {/* 学完马上能做什么 */}
                                        {objectives["学完马上能做什么"] && (
                                          <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-2 text-pink-300 text-xs">
                                            <span className="font-bold text-pink-200">学完马上能做什么:</span> {objectives["学完马上能做什么"]}
                                          </div>
                                        )}
                                        {/* 未来能做的大事 */}
                                        {objectives["未来能做的大事"] && (
                                          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 text-yellow-300 text-xs">
                                            <span className="font-bold text-yellow-200">未来能做的大事:</span> {objectives["未来能做的大事"]}
                                          </div>
                                        )}
                                        {/* 知识进阶路线 */}
                                        {objectives["知识进阶路线"] && (
                                          <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-2 text-indigo-300 text-xs">
                                            <span className="font-bold text-indigo-200">知识进阶路线:</span> {objectives["知识进阶路线"]}
                                          </div>
                                        )}
                                      </>
                                    );
                                  } catch (error) {
                                    // JSON解析失败时显示原始文本
                                    return <span className="text-gray-300">{lesson.learning_objectives}</span>;
                                  }
                                })()}
                              </div>
                            ) : (
                              <span className="text-gray-500 italic">暂无课程说明</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default StudentPortal;
